'use client'

import { useMemo, useState } from 'react'
import type { TFunction } from 'i18next'
import LightningIcon from 'src/app/assets/icons/ico-bolt-lightning.svg'
import CheckIcon from 'src/app/assets/icons/ico-check.svg'
import CopyIcon from 'src/app/assets/icons/ico-copy.svg'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import type { Bet } from 'src/types/domain/Bet'
import type { Marble } from 'src/types/domain/Marble'
import type { Race } from 'src/types/domain/Race'
import { getVisualPotentialPayout } from 'src/utils/betUtils'
import { displayMoney } from 'src/utils/displayAmount'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import {
  hasRoundFairness,
  shortenHash
} from 'src/components/CrashMultiplierHistory/crashRoundFairness'
import './birdie-board.scss'
import { birdieBetCountLabel, birdieBetHistoryCopy, birdiePendingNote } from './betHistoryCopy'
import {
  betState,
  birdieRoundHead,
  resultWithheld,
  slipMoney,
  slipStacked,
  type BirdieBetRound,
  type BirdieRoundHead
} from './betHistoryRounds'
import { puttResultVisible } from './boardPhase'
import { BirdieBallDefs } from './GolfBall'
import { PuttGlyph } from './PuttGlyph'
import { RoundMark } from './RoundMark'
import {
  familyForKey,
  formatMultiplierText,
  glyphsForKey,
  type PuttMark,
  type Translate
} from './tileViewModel'
import { useBirdieRevealClock } from './useBirdieRevealClock'
import { useBirdieTranslate } from './useBirdieTranslate'

/**
 * The bet history's ENTRIES, for a Birdie game: one bet slip per round (B26d).
 *
 * B19b's rule, again: one surface per job, and Birdie is a RENDERING inside it.
 * `<BetHistoryPane>` keeps its chassis, its store read, its Supabase race
 * hydration, its clipboard handler, its paging and its empty state; only the
 * row changes — one entry per round here, drawn on the Past Games card shell
 * (`.birdie-rc--compact`) as a slip: a context line (round, when — or the
 * Lightning mark — and the verdict), the golfer and the result in one quiet
 * line, then ONE grid — a row per bet in four aligned columns, pick · stake ·
 * odds · return — with the totals in the same columns, and the round's hash
 * under it. Entries are not collapsible: every bet is on the slip, and a bet
 * unfolds its facts in place on tap. Nothing in this file is reachable from
 * any other game.
 *
 * Owner sign-off 2026-09-14 on the mockup, after a three-way design panel;
 * the CSS is the proposal's, lifted verbatim (`birdie-board.scss`), and the
 * classes below are the contract it was written against.
 *
 * THE TWO DEFECTS B26 FIXED, and why the fix is here and not on the wire. A
 * Birdie chip submits with no `betType`, so `useChipPlacementBetSubmit` falls
 * back to `type: 'PickWinner', selectedItems: [optionId]`. The flat row then
 * (1) printed the wire type's own label through `<BetTypeLabel>` and (2) drew
 * an EMPTY pick strip, because `renderBetMarbles` looks the selection up as a
 * marble to skin and a Birdie option's colour is a canonical key (`IN2`,
 * `HMH`) with no marble drawing behind it. `selectedItems: [optionId]` is
 * what SETTLEMENT reads, so the wire shape does not move: the family and the
 * balls are read off the option's own key instead, through the SAME
 * `familyForKey` / `glyphsForKey` vocabulary the board tiles draw.
 *
 * WHAT IS PASSED IN RATHER THAN REIMPLEMENTED: both time formatters, the
 * support-tool deep link and the clipboard handler with its copied state.
 * Every one of them already exists in the pane, several carry a locale that
 * took work to get right, and a second copy here would be a second thing to
 * keep in step.
 *
 * THE FAIRNESS OPENER (B33). The shared `CrashRoundModal` learned the Birdie
 * family and mounts the board's own fairness body for a Birdie round, so an
 * entry carries its own provably-fair opener (the flat row's own button label,
 * raised as a literal call in `betHistoryCopy.ts`), wired to the pane's
 * existing `setFairnessRaceId` through `onOpenFairness` — the same way the
 * copy handler is passed in. It is gated the way the flat row gates
 * (`hasRoundFairness`, on a settled round) AND on the board's reveal hold, and
 * it sits under the round's hash line.
 *
 * THE LOOKUP LINK IS PER BET (B26e). The pane's support deep link carries the
 * BET id, and players look up the specific bet — so it is drawn inside each
 * unfolded bet's facts, one link per bet, and the round line keeps the hash
 * alone rather than pointing the whole round at its newest bet. Replay and
 * proof are different things, on different lines.
 *
 * THE FACES (B26e). The figures and the unfolded facts wear the page's BODY
 * face — inherited, never named here: the widget applies it to <body> and
 * defines no custom property for it — and the display face is declared on the
 * caps labels alone (`birdie-board.scss`). Inter is narrower than the display
 * face, so the entry's stacking threshold was re-derived on it — and stays
 * where it was (`betHistoryRounds.SLIP_STACK_CHARS`).
 */

type Props = Readonly<{
  /** The rounds the pane's paging left visible, newest first. */
  rounds: readonly BirdieBetRound[]
  /** The pane's Supabase-hydrated races — the golfer, the odds and the result. */
  races: readonly Race[]
  /** The game's twelve option rows; `color` is the canonical key. */
  marbles: readonly Marble[]
  /** The pane's relative-time formatter, dayjs locale and all. */
  formatTimeAgo: (isoTimestamp: string) => string
  /** The pane's absolute-date formatter, already bound to the language. */
  formatBetTime: (isoTimestamp: string) => string
  /** The pane's support-tool deep link for one bet. */
  verifyUrl: (bet: Bet) => string
  /** The pane's clipboard handler, and the per-key "Copied!" state it drives. */
  onCopy: (value: string, key: string) => void
  copiedStates: Readonly<Record<string, boolean>>
  /** The pane's `setFairnessRaceId` — opens the shared round-fairness modal. */
  onOpenFairness: (raceId: string) => void
}>

/** A bet row's picked balls, px (the mockup's). */
const PICK_BALL_PX = 12
/** …and the head's result balls, one step up: the round's own three putts. */
const RESULT_BALL_PX = 14

/** The words this file needs, all raised as literal `t()` calls elsewhere. */
type BetHistoryCopy = ReturnType<typeof birdieBetHistoryCopy>

/**
 * The lookup link's trailing arrow. The pane's own key carries it in every
 * locale as part of the sentence, so a screen reader announced "north east
 * arrow" after the label; the mockup draws it as decoration, hidden. Split
 * off here rather than minted as a second key — and a locale without it
 * simply gets the hidden arrow drawn after its label. Plain regex: the
 * widget targets es5.
 */
const REPLAY_ARROW = /\s*↗\s*$/

/**
 * One bet's price AS IT WAS TAKEN.
 *
 * 🚨 NOT the round's current odds and not the board's: Birdie reprices every
 * round, so the option a bet is on is worth something different by the time
 * anyone reads the history. `potentialPayout` is stored at bet time and for a
 * fixed-odds bet is stake x odds — the flat row's own Multiplier line reads it
 * exactly the same way (the crash caveat about `potentialPayout` being the
 * operator liability ceiling does not apply to Birdie, whose bets have a real
 * fixed payout). FLOORED to 2 dp by `formatMultiplierText`, so the quoted price
 * can never read higher than the payout it is derived from (QA-60088).
 */
export function betPriceText(bet: Bet): string | undefined {
  if (!(bet.betAmount > 0) || !(bet.potentialPayout > 0)) return undefined
  return formatMultiplierText(bet.potentialPayout / bet.betAmount)
}

/**
 * The bet's canonical key (`IN2`, `HMH`) from its single selected option.
 *
 * The annotation is deliberate. `BetDetails` types the forecast markets'
 * selections as fixed-length tuples, so TypeScript believes index 0 is always a
 * string — but this list arrives off the wire, and an empty one would hand the
 * lookup an `undefined` index and read a garbage key.
 */
export const betOptionKey = (
  bet: Bet,
  keyById: Readonly<Record<string, string>>
): string | undefined => {
  const optionId: string | undefined = bet.betDetails.selectedItems[0]
  if (optionId === undefined) return undefined
  return keyById[optionId]
}

/** GROUPED, like every figure on the slip — `displayMoney`'s second argument. */
const money = (amount: number): string => displayMoney(amount.toString(), true)

function Balls({
  marks,
  size,
  className
}: Readonly<{ marks: readonly PuttMark[]; size: number; className: string }>) {
  return (
    <span aria-hidden="true" className={className}>
      {marks.map((mark, index) => (
        // Fixed length, immutable order: a placed bet's picked pattern cannot
        // change under the key, and neither can a settled round's result.
        // eslint-disable-next-line react/no-array-index-key -- fixed-length, immutable putt order
        <PuttGlyph key={index} mark={mark} size={size} />
      ))}
    </span>
  )
}

/**
 * One money figure, by the slip's rule (`slipMoney`): a glyph glues to the
 * figure; an alphabetic code is its own token beside it, and in a `--stacked`
 * entry the CSS puts every code IN THE GRID under its figure — the unfolded
 * facts, with the room, keep the inline form. Always wrapped, code or not, so
 * the stacked rule reaches every figure of the grid the same way.
 */
function Money({ symbol, amount }: Readonly<{ symbol: string; amount: string }>) {
  const parts = slipMoney(symbol, amount)
  return (
    <span className="birdie-slip__money">
      {parts.code === undefined ? null : (
        <>
          <span className="birdie-slip__ccy">{parts.code}</span>{' '}
        </>
      )}
      <span className="birdie-slip__num">{parts.figure}</span>
    </span>
  )
}

/** The gold bolt seal — the board's own, one size down (the mockup's). */
function LightningSeal() {
  return (
    <span aria-hidden="true" className="birdie-slip__seal">
      <LightningIcon />
    </span>
  )
}

/** The flat row's copy affordance, verbatim — same icons, same two labels. */
function CopyButton({
  copied,
  label,
  onCopy
}: Readonly<{ copied: boolean; label: string; onCopy: () => void }>) {
  return (
    <button
      aria-label={label}
      className="default-btn copy-btn birdie-slip__copy"
      onClick={(event) => {
        event.stopPropagation()
        onCopy()
      }}
      title={label}
      type="button"
    >
      {copied ? (
        <CheckIcon className="copy-btn__icon copy-btn__icon--done" />
      ) : (
        <CopyIcon className="copy-btn__icon" />
      )}
    </button>
  )
}

/**
 * The golfer, or their initials, on the slip's 22-px disc.
 *
 * The round card's avatar is not reused: it takes no size and draws the
 * card's 33-px ringed disc. The headshot itself wears the card's own crop
 * rule (`birdie-rc__shot`), so a picture is drawn the same way on both. The
 * FAILED url is tracked rather than a bare boolean, exactly as the capsule and
 * the card do it: the next round's headshot is retried without an effect.
 * Decorative — the name beside it names the golfer.
 */
function SlipAvatar({ head }: Readonly<{ head: BirdieRoundHead }>) {
  const [failedShotUrl, setFailedShotUrl] = useState<string | undefined>(undefined)
  const url =
    head.headshotUrl !== undefined && head.headshotUrl !== failedShotUrl
      ? head.headshotUrl
      : undefined
  return (
    <span aria-hidden="true" className="birdie-slip__avatar">
      {url === undefined ? (
        head.initials
      ) : (
        // eslint-disable-next-line @next/next/no-img-element -- CDN headshot, no loader configured
        <img
          alt=""
          className="birdie-rc__shot"
          onError={() => {
            setFailedShotUrl(head.headshotUrl)
          }}
          src={url}
        />
      )}
    </span>
  )
}

export type BirdieSlipBetProps = Readonly<{
  bet: Bet
  /** The canonical key of the option this bet backed, when it resolves. */
  optionKey: string | undefined
  /** That option's localised name — `Hole · Miss · Hole`, `Two in`. */
  optionName: string | undefined
  /**
   * The option's UNBOOSTED price on the round's own committed board, when the
   * round's Lightning boost was on it — the round card's `basePrice`. The row
   * wears the seal only when the price it was taken at differs from this.
   */
  basePrice: string | undefined
  copy: BetHistoryCopy
  /** The bet currency's symbol, resolved once by the entry. */
  symbol: string
  open: boolean
  /**
   * True while the round's result is withheld (`resultWithheld`): the bet is
   * drawn as still running whatever its status — no gold bar, no money.
   */
  withheld: boolean
  /**
   * The pane's support-tool deep link for THIS bet — the replay, the
   * fairness card and one-click verify, with the bet id in the query (B26e).
   * Players look up the specific bet, so the link is the bet's own and lives
   * at the foot of its facts, not once per round.
   */
  lookupUrl: string
  onToggle: () => void
  formatBetTime: (isoTimestamp: string) => string
  onCopy: (value: string, key: string) => void
  copiedStates: Readonly<Record<string, boolean>>
}>

/**
 * ONE bet on the slip: a real button that is a grid row — the pick (balls and
 * the market's name), the stake, the price it was taken at, and what came
 * back — followed, when open, by its facts in the same box, with the bet's
 * own lookup link last.
 *
 * Exported so it can be rendered on its own: the family label, the glyph count
 * per family and the price are the three facts B26 exists to correct, and the
 * open state is one a static render of the whole list can never reach.
 */
export function BirdieSlipBet({
  bet,
  optionKey,
  optionName,
  basePrice,
  copy,
  symbol,
  open,
  withheld,
  lookupUrl,
  onToggle,
  formatBetTime,
  onCopy,
  copiedStates
}: BirdieSlipBetProps) {
  const family = optionKey === undefined ? undefined : familyForKey(optionKey)
  const marks = optionKey === undefined ? undefined : glyphsForKey(optionKey)
  const price = betPriceText(bet)
  // Inside the hold a settled bet is drawn as still running — see
  // `resultWithheld`. The status the payout helper reads follows it: a
  // pending bet's facts show its potential payout, which is no spoiler.
  const status = withheld ? 'pending' : bet.status
  const state = betState(status)
  // The market's real name, NOT the wire type's label. An EXACT pick is a
  // sequence and reads left to right; a COUNT pick says only how many dropped.
  const lane =
    family === 'exact'
      ? copy.exactOrder
      : family === 'count'
        ? copy.anyOrder
        : // B36: the round-type pair names itself — the option IS the market here.
          family === 'round'
          ? optionKey === 'FIRE'
            ? copy.fireRound
            : copy.frostRound
          : ''
  const lit = basePrice !== undefined && price !== undefined && price !== basePrice
  const stake = money(bet.betAmount)
  // `-` is the payout helper's own "nothing came back" for a lost or cancelled
  // bet. Printed bare in the facts below: a currency symbol in front of it
  // would read as an amount ("$-") rather than as an absence. A figure goes
  // through `<Money>` like every other on the slip — grouped, the code its
  // own token — so an open entry never shows the same money two ways.
  const fullPayout = getVisualPotentialPayout(bet, status, false, true)

  return (
    <>
      <button
        aria-expanded={open}
        className={`birdie-slip__bet birdie-slip__bet--${state}`}
        onClick={onToggle}
        type="button"
      >
        <span className="birdie-slip__cell birdie-slip__pick">
          <span className="birdie-slip__sel">
            {family === 'round' ? (
              // no balls: a round-type bet describes no putts. The mark carries the pick, exactly as
              // it does on the tile and in the live feed, so one vocabulary covers all three surfaces.
              <span className={`birdie-slip__mark birdie-slip__mark--${(optionKey ?? '').toLowerCase()}`}>
                <RoundMark kind={optionKey === 'FIRE' ? 'FIRE' : 'FROST'} size={PICK_BALL_PX + 2} />
              </span>
            ) : marks === undefined ? null : (
              <Balls className="birdie-slip__marks" marks={marks} size={PICK_BALL_PX} />
            )}
            {/* The visible tag is the family alone; the hidden sentence names
                the family AND the option, so a reader hears the pick once,
                whole, rather than the family twice. */}
            {lane === '' ? null : (
              <span aria-hidden="true" className="birdie-slip__tag">
                {lane}
              </span>
            )}
            {lane === '' || optionName === undefined ? null : (
              <span className="birdie-sr-only">
                {lane} · {optionName}
              </span>
            )}
          </span>
        </span>
        <span className="birdie-slip__cell birdie-slip__stake">
          <Money amount={stake} symbol={symbol} />
        </span>
        <span
          className={`birdie-slip__cell birdie-slip__odds${lit ? ' birdie-slip__odds--lit' : ''}`}
        >
          {price === undefined ? null : lit ? (
            <>
              <span className="birdie-slip__lit">
                <LightningSeal />
                <b className="birdie-slip__price">{price}</b>
              </span>
              <span className="birdie-slip__base">{basePrice}</span>
            </>
          ) : (
            <b className="birdie-slip__price">{price}</b>
          )}
        </span>
        <span className="birdie-slip__cell birdie-slip__retcell">
          {state === 'won' ? (
            <span className="birdie-slip__ret birdie-slip__ret--won">
              <Money amount={getVisualPotentialPayout(bet, status)} symbol={symbol} />
            </span>
          ) : state === 'void' ? (
            // The refund: a voided bet's stake came back, so the return column
            // says so in money, not in a word.
            <span className="birdie-slip__ret birdie-slip__ret--void">
              <Money amount={stake} symbol={symbol} />
            </span>
          ) : (
            <span className={`birdie-slip__state birdie-slip__state--${state}`}>
              {state === 'pending' ? copy.pending : copy.lost}
            </span>
          )}
        </span>
      </button>

      {!open ? null : (
        <div className="birdie-slip__detail">
          <div className="birdie-slip__facts">
            <div className="info-entry">
              <span className="label">{copy.betId}</span>
              <span className="value" title={bet.id}>
                #{shortenHash(bet.id)}
              </span>
              <CopyButton
                copied={copiedStates[`bet-${bet.id}`] === true}
                label={copiedStates[`bet-${bet.id}`] === true ? copy.copied : copy.copy}
                onCopy={() => {
                  onCopy(bet.id, `bet-${bet.id}`)
                }}
              />
            </div>
            {/* The market in words: the family, then the option's own localised
                name. Both come from `marbleColors`, which is where the board's
                tiles read them too, so a row and a tile can never name the same
                option differently. */}
            {optionName === undefined ? null : (
              <div className="info-entry">
                <span className="label">{copy.market}</span>
                <span className="value">
                  {lane} · {optionName}
                </span>
              </div>
            )}
            <div className="info-entry">
              <span className="label">{copy.betTime}</span>
              <span className="value">{formatBetTime(bet.createdAt)}</span>
            </div>
            <div className="info-entry">
              <span className="label">{copy.betAmount}</span>
              <span className="value">
                <Money amount={stake} symbol={symbol} />
              </span>
            </div>
            {price === undefined ? null : (
              <div className="info-entry">
                <span className="label">{copy.multiplier}</span>
                <span className="value">{price}</span>
              </div>
            )}
            {/* The FULL stored payout, not the 2 dp the row above shows: money
                is stored at 5-decimal wire precision and displayed truncated at
                2, so a 40.71978 payout reads "40.71" on screen while the wallet
                is credited 40.71978 — which operators report as a discrepancy.
                The flat row's expanded block does exactly this, for exactly this
                reason. */}
            <div className="info-entry">
              <span className="label">{copy.wonAmount}</span>
              <span className="value">
                {fullPayout === '-' ? fullPayout : <Money amount={fullPayout} symbol={symbol} />}
              </span>
            </div>
            {/* This bet's own lookup, last and right — the pane's link, with
                the pane's own wording. */}
            <a
              className="birdie-slip__lookup"
              href={lookupUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              {copy.viewReplay.replace(REPLAY_ARROW, ' ')}
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      )}
    </>
  )
}

export type BirdieBetRoundFactsProps = Readonly<{
  round: BirdieBetRound
  /** The pane's hydrated race for this round, once it has landed. */
  race: Race | undefined
  copy: BetHistoryCopy
  onCopy: (value: string, key: string) => void
  copiedStates: Readonly<Record<string, boolean>>
  /** The rows' reveal clock (`useBirdieRevealClock`), ms — the hold's "now". */
  nowMs: number
  onOpenFairness: (raceId: string) => void
}>

/**
 * The round's own facts, under the slip: the id every bet on the round shares
 * (shortened, copied whole) and — B33 — the opener for its proof, full width
 * beneath it. The lookup link is not here: it is each bet's own (B26e).
 *
 * Exported so it can be rendered on its own: the fairness gate is exactly the
 * kind of thing a test has to be able to see.
 */
export function BirdieBetRoundFacts({
  round,
  race,
  copy,
  onCopy,
  copiedStates,
  nowMs,
  onOpenFairness
}: BirdieBetRoundFactsProps) {
  // The flat row's own gate — a settled round that published a commitment or a
  // reveal — AND the board's anti-spoiler hold, on the same predicate and the
  // same constant the putt marks in the head and the past-rounds strip use.
  // The body shows hashes, not putts, but the verifier link it opens carries
  // the winning option in its query string (`verifierLink`'s `order`), which
  // IS the round's result: so the opener appears at the instant the marks do,
  // and not a second before. A round that has not settled has no settled-at
  // and fails the predicate, which keeps the flat row's "still running" rule.
  const settled = race?.raceEvents.find((event) => event.eventType === 'race_settled')
  const settledAt = settled === undefined ? undefined : new Date(settled.eventTimestamp).getTime()
  const provable =
    race !== undefined && hasRoundFairness(race) && puttResultVisible({ settledAt, now: nowMs })

  return (
    <>
      <div className="birdie-slip__links">
        <span className="birdie-slip__hash">
          <span className="birdie-slip__hash-text" title={round.raceId}>
            #{shortenHash(round.raceId)}
          </span>
          <CopyButton
            copied={copiedStates[`race-${round.raceId}`] === true}
            label={copiedStates[`race-${round.raceId}`] === true ? copy.copied : copy.copy}
            onCopy={() => {
              onCopy(round.raceId, `race-${round.raceId}`)
            }}
          />
        </span>
      </div>
      {!provable ? null : (
        <button
          className="birdie-slip__fair"
          onClick={() => {
            onOpenFairness(round.raceId)
          }}
          type="button"
        >
          {copy.provablyFair}
        </button>
      )}
    </>
  )
}

export type BirdieSlipEntryProps = Readonly<{
  round: BirdieBetRound
  /** The pane's hydrated race for this round, once it has landed. */
  race: Race | undefined
  /** The game's option rows, for the round's settled facts. */
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
  /** Option id → canonical key, built once by the list. */
  keyById: Readonly<Record<string, string>>
  copy: BetHistoryCopy
  /** The language-pinned translator, for the two count-dependent labels. */
  t: Translate
  /** The same, under i18next's branded type — the option names' reader. */
  translate: TFunction
  /** The rows' reveal clock, ms. */
  nowMs: number
  /** The bet whose facts are unfolded, if any. */
  openBetId: string | null
  onToggleBet: (betId: string) => void
  formatTimeAgo: (isoTimestamp: string) => string
  formatBetTime: (isoTimestamp: string) => string
  verifyUrl: (bet: Bet) => string
  onCopy: (value: string, key: string) => void
  copiedStates: Readonly<Record<string, boolean>>
  onOpenFairness: (raceId: string) => void
}>

/**
 * ONE round, as a slip. Hook-free apart from the avatar's retry state, so the
 * width proof and the tests can render it with any open bet they like.
 */
export function BirdieSlipEntry({
  round,
  race,
  marbles,
  keyById,
  copy,
  t,
  translate,
  nowMs,
  openBetId,
  onToggleBet,
  formatTimeAgo,
  formatBetTime,
  verifyUrl,
  onCopy,
  copiedStates,
  onOpenFairness
}: BirdieSlipEntryProps) {
  const head = birdieRoundHead({ race, marbles, nowMs })
  const newest = round.bets[0]
  const symbol = getCurrencySymbol(newest?.currency ?? '')

  // The verdict the entry PRINTS: the bets' own once the result may be shown,
  // pending while it is withheld — the chip, every row and the totals on the
  // same instant as the head's marks. See `resultWithheld`.
  const withheld = resultWithheld({ race, verdict: round.verdict, nowMs })
  const verdict = withheld ? 'pending' : round.verdict

  // The round number is an IDENTIFIER, not a quantity, so it never goes
  // through a grouping formatter (the card's own rule: "#1,982" is not a
  // round). Phrased as the Past Games card beside it phrases it, and with the
  // card's fallback for a round the pane has not hydrated yet.
  const roundLabel = `${copy.round} ${
    typeof race?.round === 'number' ? `#${race.round}` : round.raceId.slice(0, 8)
  }`

  const verdictWord =
    verdict === 'won'
      ? copy.won
      : verdict === 'pending'
        ? copy.pending
        : verdict === 'void'
          ? copy.cancelled
          : copy.lost

  const outcomeName =
    head?.countKey === undefined ? undefined : getTranslatedMarbleColor(head.countKey, translate)
  const patternLine =
    head?.patternKey === undefined
      ? undefined
      : getTranslatedMarbleColor(head.patternKey, translate)

  const boostByKey = new Map((head?.boosts ?? []).map((boost) => [boost.key, boost]))

  // ONE sum, formatted ONCE, through the panel's own helper — the float-dust
  // rule in `betHistoryRounds.ts`.
  const staked = money(round.totalStaked)
  const returned = money(round.totalReturned)
  // Every money string the entry prints, for the one stacking decision.
  const amounts = [staked, returned]
  for (const bet of round.bets) {
    amounts.push(money(bet.betAmount))
    if (betState(bet.status) === 'won') amounts.push(getVisualPotentialPayout(bet, bet.status))
  }
  const stacked = slipStacked(symbol, amounts)

  return (
    <article
      className={`birdie-rc birdie-rc--compact birdie-slip birdie-slip--${verdict}${
        stacked ? ' birdie-slip--stacked' : ''
      }`}
    >
      {/* Context line: which round, and when — or, on a boosted round, the
          gold mark, which takes the same slot so the two can never collide —
          and the verdict, right. */}
      <div className="birdie-rc__eyebrow birdie-slip__eyebrow">
        <span className="birdie-slip__ctx">
          <span className="birdie-rc__round-id">{roundLabel}</span>
          <span aria-hidden="true" className="birdie-slip__sep">
            ·
          </span>
          {head?.lightning === true ? (
            <span className="birdie-slip__bolt">
              <LightningSeal />
              {copy.lightning}
            </span>
          ) : (
            <span className="birdie-slip__when">{formatTimeAgo(round.placedAt)}</span>
          )}
        </span>
        <span className={`birdie-slip__verdict birdie-slip__verdict--${verdict}`}>
          {verdictWord}
        </span>
      </div>

      {/* The golfer, and — once the hold has elapsed — the three putts with the
          outcome named beside them. While the result is withheld the line is
          the golfer alone; the verdict chip already says the round is open. */}
      {head === undefined ? null : (
        <div className="birdie-slip__head">
          <SlipAvatar head={head} />
          <span className="birdie-slip__name">{head.golferName}</span>
          {head.marks.length === 0 ? null : (
            <span className="birdie-slip__result">
              <Balls className="birdie-slip__balls" marks={head.marks} size={RESULT_BALL_PX} />
              {outcomeName === undefined ? null : (
                <b className="birdie-slip__outcome">{outcomeName}</b>
              )}
              {patternLine === undefined ? null : (
                <span className="birdie-sr-only">{patternLine}</span>
              )}
            </span>
          )}
        </div>
      )}

      {/* The slip: ONE grid — the headings, a row per bet, the totals. */}
      <div className="birdie-slip__table">
        <div aria-hidden="true" className="birdie-slip__cols">
          <span>{birdieBetCountLabel(t, round.betCount)}</span>
          <span>{copy.stake}</span>
          <span>{copy.odds}</span>
          <span>{copy.returns}</span>
        </div>
        {round.bets.map((bet) => {
          const optionKey = betOptionKey(bet, keyById)
          const boost = optionKey === undefined ? undefined : boostByKey.get(optionKey)
          return (
            <BirdieSlipBet
              basePrice={boost === undefined ? undefined : formatMultiplierText(boost.base)}
              bet={bet}
              copiedStates={copiedStates}
              copy={copy}
              formatBetTime={formatBetTime}
              key={bet.id}
              lookupUrl={verifyUrl(bet)}
              onCopy={onCopy}
              onToggle={() => {
                onToggleBet(bet.id)
              }}
              open={openBetId === bet.id}
              optionKey={optionKey}
              optionName={
                optionKey === undefined ? undefined : getTranslatedMarbleColor(optionKey, translate)
              }
              symbol={symbol}
              withheld={withheld}
            />
          )
        })}
        <div className="birdie-slip__total">
          <span className="birdie-slip__cell">
            <span className="birdie-slip__total-label">{copy.total}</span>
            {/* A settled-looking entry with bets still running says so here,
                under the label — the return beside it may still move. */}
            {verdict === 'won' && round.pendingCount > 0 ? (
              <span className="birdie-slip__note">{birdiePendingNote(t, round.pendingCount)}</span>
            ) : null}
          </span>
          <span className="birdie-slip__cell birdie-slip__stake">
            <Money amount={staked} symbol={symbol} />
          </span>
          <span className="birdie-slip__cell" />
          <span className="birdie-slip__cell birdie-slip__retcell">
            {verdict === 'won' ? (
              <span className="birdie-slip__ret birdie-slip__ret--won">
                <Money amount={returned} symbol={symbol} />
              </span>
            ) : (
              <span className={`birdie-slip__state birdie-slip__state--${verdict}`}>
                {verdictWord}
              </span>
            )}
          </span>
        </div>
      </div>

      <BirdieBetRoundFacts
        copiedStates={copiedStates}
        copy={copy}
        nowMs={nowMs}
        onCopy={onCopy}
        onOpenFairness={onOpenFairness}
        race={race}
        round={round}
      />
    </article>
  )
}

export function BirdieBetHistoryRows({
  rounds,
  races,
  marbles,
  formatTimeAgo,
  formatBetTime,
  verifyUrl,
  onCopy,
  copiedStates,
  onOpenFairness
}: Props) {
  const { t, translate } = useBirdieTranslate()
  const copy = useMemo(() => birdieBetHistoryCopy(t), [t])
  // The anti-spoiler clock: ONE re-render, at the instant the newest settled
  // round's own hold elapses. See `useBirdieRevealClock`.
  const nowMs = useBirdieRevealClock(races)

  const [openBetId, setOpenBetId] = useState<string | null>(null)

  /** Option id → canonical key. The twelve rows carry it as their colour. */
  const keyById = useMemo(() => {
    const out: Record<string, string> = {}
    for (const marble of marbles) out[marble.uuid] = marble.color.toUpperCase()
    return out
  }, [marbles])

  const raceById = useMemo(() => {
    const out = new Map<string, Race>()
    for (const race of races) out.set(race.uuid, race)
    return out
  }, [races])

  if (rounds.length === 0) {
    return (
      <div className="entry entry--empty">
        <span>{copy.empty}</span>
      </div>
    )
  }

  return (
    <>
      {/* The pane is outside `.birdie-overlay`, so the balls' gradients are
          declared again here or every glyph in a row draws unpainted. Fixed
          ids, identical definitions, therefore idempotent (`GolfBall.tsx`). */}
      <BirdieBallDefs />
      {rounds.map((round) => (
        <BirdieSlipEntry
          copiedStates={copiedStates}
          copy={copy}
          formatBetTime={formatBetTime}
          formatTimeAgo={formatTimeAgo}
          key={round.raceId}
          keyById={keyById}
          marbles={marbles}
          nowMs={nowMs}
          onCopy={onCopy}
          onOpenFairness={onOpenFairness}
          onToggleBet={(betId) => {
            setOpenBetId(openBetId === betId ? null : betId)
          }}
          openBetId={openBetId}
          race={raceById.get(round.raceId)}
          round={round}
          t={t}
          translate={translate}
          verifyUrl={verifyUrl}
        />
      ))}
    </>
  )
}
